import { useMemo } from 'react';
import {
  Autocomplete,
  FormControls,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import { ValueLabelOption } from '@elementary/common-ui/types';
import { Controller, UseFormReturn } from 'react-hook-form';
import { AlertDestinationFormData } from 'shared/types';
import {
  isValidEmail,
  extractEmailsFromText,
  hasInvalidEmails,
} from 'shared/utils/validators';

const EMAIL_SEPARATORS = /[,;\s]+/;

type Props = {
  methods: UseFormReturn<AlertDestinationFormData, any, undefined>;
  emails: ValueLabelOption[];
  inputValue: string;
  onInputValueChange: (value: string) => void;
  autoSubmit?: boolean;
  onAutoSubmit?: () => void;
};

const EmailsAutocompleteField = ({
  methods,
  emails,
  inputValue,
  onInputValueChange,
  autoSubmit,
  onAutoSubmit,
}: Props) => {
  const destination = methods.watch('destination');

  const selectedEmails = useMemo(
    () => (Array.isArray(destination) ? destination : []),
    [destination],
  );

  const availableEmails = useMemo(() => {
    const selectedEmailValues = selectedEmails.map((item) => item.value);
    return emails.filter(
      (emailOption) => !selectedEmailValues.includes(emailOption.value),
    );
  }, [emails, selectedEmails]);

  const appendEmails = (
    current: ValueLabelOption[],
    newEmails: string[],
    onChange: (value: ValueLabelOption[]) => void,
  ) => {
    const existingEmails = new Set(current.map((item) => item.value));
    const additions: ValueLabelOption[] = [];

    newEmails
      .map((email) => email.trim())
      .forEach((email) => {
        if (email && !existingEmails.has(email)) {
          existingEmails.add(email);
          additions.push({ value: email, label: email });
        }
      });

    if (additions.length > 0) {
      onChange([...current, ...additions]);
    }
  };

  const handlePaste = (
    event: React.ClipboardEvent<HTMLDivElement>,
    currentValue: ValueLabelOption[],
    onChange: (value: ValueLabelOption[]) => void,
  ) => {
    const extractedEmails = extractEmailsFromText(
      event.clipboardData.getData('text'),
    );

    if (extractedEmails.length > 0) {
      event.preventDefault();
      appendEmails(currentValue, extractedEmails, onChange);
    }
  };

  return (
    <FormControls.InputWrapper style={{ flex: 1 }}>
      <Controller
        name="destination"
        control={methods.control}
        rules={{ required: requiredLabel }}
        render={({ field }) => {
          const currentValue = Array.isArray(field.value) ? field.value : [];

          return (
            <div onPaste={(e) => handlePaste(e, currentValue, field.onChange)}>
              <Autocomplete
                value={currentValue}
                inputValue={inputValue}
                size="large"
                loadingText="Loading user emails..."
                placeholder="Type or paste emails, or select from the list"
                options={availableEmails}
                freeSolo
                multiple
                disabled={!methods.watch('alertProvider')}
                valueValidator={isValidEmail}
                invalidMessage="Please enter a valid email address."
                onChange={(_, val: (string | ValueLabelOption)[]) => {
                  const seen = new Set<string>();
                  const normalizedVal = val
                    .map((item) =>
                      typeof item === 'string'
                        ? { value: item.trim(), label: item.trim() }
                        : item,
                    )
                    .filter((item) => {
                      if (!item.value || seen.has(item.value)) return false;
                      seen.add(item.value);
                      return true;
                    });
                  field.onChange(normalizedVal);
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (
                    reason === 'input' &&
                    EMAIL_SEPARATORS.test(newInputValue)
                  ) {
                    const tokens = newInputValue.split(EMAIL_SEPARATORS);
                    const remaining = tokens.pop() ?? '';
                    appendEmails(currentValue, tokens, field.onChange);
                    onInputValueChange(remaining);
                    return;
                  }
                  onInputValueChange(newInputValue);
                }}
                onBlur={() => {
                  if (!autoSubmit) return;

                  const pending = inputValue.trim();
                  const hasValidPending =
                    pending !== '' && isValidEmail(pending);
                  const hasContent = currentValue.length > 0 || hasValidPending;

                  if (
                    hasContent &&
                    !hasInvalidEmails(currentValue) &&
                    (pending === '' || isValidEmail(pending))
                  ) {
                    onAutoSubmit?.();
                  }
                }}
              />
            </div>
          );
        }}
      />
    </FormControls.InputWrapper>
  );
};

export default EmailsAutocompleteField;
